import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Save } from "lucide-react";
import ExcelGrid from "components/ExcelGrid";
import FileManagerModal from "components/FileManager/FileManagerModal";
import FileManagerPanel from "components/FileManager/FileManagerPanel";
import Page from "components/Page";
import SectionCard from "components/SectionCard";
import { BrowseInput } from "components/InputStyled";
import { useDialog } from "context/DialogContext";
import { useProfile } from "context/ProfileContext";
import AllocationComponentModal from "./components/AllocationComponentModal";
import {
  useIssuesFileManager,
} from "pages/DebtStudio/components/Issues/fileManager";
import { calculateAllocationStatus, fromCents, toCents } from "./lib/allocation";
import { getClassificationCodePath } from "./lib/hierarchy";
import {
  deleteDebtAllocationComponent,
  fetchAllProjectClassificationAssignments,
  fetchClassifications,
  fetchDebtAllocationPortfolio,
  fetchDebtAllocationWorkspace,
  fetchDebtStudioIssues,
  fetchProjects,
  replaceDebtAllocationAmounts,
  saveDebtAllocationComponent,
  summarizeDebtStudioIssue,
} from "./services/capitalFlowService";

const currency = (value) => Number(value || 0).toLocaleString(undefined, {
  style: "currency", currency: "USD", minimumFractionDigits: 2,
});
const amount = (value) => Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const componentKey = (id) => `component_${String(id).replaceAll("-", "_")}`;

function componentDetails(component, projects, classifications, assignments) {
  const project = projects.find((item) => item.id === component.project_id);
  const paths = assignments.filter((item) => item.component_id === component.id).map((assignment) => {
    const classification = classifications.find((item) => item.id === assignment.classification_id);
    return classification ? `${classification.code?.trim() || classification.name}: ${getClassificationCodePath(classification.values, assignment.classification_value_id)}` : "";
  }).filter(Boolean);
  return {
    projectName: project?.project_name || "Unknown Project",
    projectCode: project?.project_code || "",
    paths,
    label: component.component_code || project?.project_name || "Allocation Component",
  };
}

function getIssuerName(record) {
  return record?.issuer_name || record?.issuers?.legal_name || record?.issuerNameRaw || "";
}

function getIssueName(record) {
  return record?.deal_name || record?.issue_name || record?.name || "(Untitled)";
}

function buildIssueAllocationStatuses(issues, portfolio) {
  const issueIdByComponentId = new Map(
    (portfolio.components || []).map((component) => [component.id, component.ds_deal_id])
  );
  const allocatedByIssueAndDate = new Map();
  const allocatedByIssue = new Map();

  (portfolio.amounts || []).forEach((allocation) => {
    const allocationIssueId = issueIdByComponentId.get(allocation.component_id);
    if (!allocationIssueId) return;
    const key = `${allocationIssueId}:${allocation.maturity_date}`;
    const allocationCents = toCents(allocation.allocated_amount || 0);
    allocatedByIssueAndDate.set(key, (allocatedByIssueAndDate.get(key) || 0) + allocationCents);
    allocatedByIssue.set(allocationIssueId, (allocatedByIssue.get(allocationIssueId) || 0) + allocationCents);
  });

  return new Map((issues || []).map((candidate) => {
    const maturityResults = (candidate.maturities || []).map((maturity) => calculateAllocationStatus({
      principalCents: maturity.principalCents ?? toCents(maturity.principalAmount || 0),
      allocatedCents: allocatedByIssueAndDate.get(`${candidate.id}:${maturity.maturityDate}`) || 0,
    }));
    const allocatedCents = allocatedByIssue.get(candidate.id) || 0;
    const matchedAllocatedCents = maturityResults.reduce((sum, result) => sum + result.allocatedCents, 0);
    return [candidate.id, {
      allocationStatus: allocatedCents === 0
        ? "none"
        : maturityResults.length > 0 && allocatedCents === matchedAllocatedCents && maturityResults.every((result) => result.status === "fully_allocated")
          ? "full"
          : "partial",
      allocationHasOverage: maturityResults.some((result) => result.status === "overallocated"),
    }];
  }));
}

export default function CapitalFlowDebtAllocations() {
  const { selectedOrg, profile } = useProfile();
  const { alert, confirm } = useDialog();
  const [issues, setIssues] = useState([]);
  const [projects, setProjects] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [projectAssignments, setProjectAssignments] = useState([]);
  const [allocationPortfolio, setAllocationPortfolio] = useState({ components: [], assignments: [], amounts: [] });
  const [workspace, setWorkspace] = useState({ components: [], assignments: [], amounts: [] });
  const [issueId, setIssueId] = useState("");
  const [gridRows, setGridRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [componentEditor, setComponentEditor] = useState(undefined);
  const [showIssueManager, setShowIssueManager] = useState(false);
  const issuesFileManager = useIssuesFileManager({ limit: 1000 });

  const tenantId = selectedOrg?.tenant_id || profile?.tenant_id;
  const issue = useMemo(() => issues.find((item) => item.id === issueId) || null, [issueId, issues]);
  const componentClassifications = useMemo(() => classifications.filter((classification) => classification.scopes.some((scope) => scope.object_type === "debt_allocation_component")), [classifications]);
  const capitalFlowIssueManager = useMemo(() => {
    const issueById = new Map(issues.map((candidate) => [candidate.id, candidate]));
    const statusByIssueId = buildIssueAllocationStatuses(issues, allocationPortfolio);
    return {
      ...issuesFileManager,
      loading: loading || issuesFileManager.loading,
      records: (issuesFileManager.records || []).filter(
        (record) => record.isLibrary || String(record.org_id || "") === String(selectedOrg?.id || "")
      ).map((record) => ({
        ...record,
        ...issueById.get(record.id),
        ...(statusByIssueId.get(record.id) || { allocationStatus: "none", allocationHasOverage: false }),
      })),
    };
  }, [allocationPortfolio, issues, issuesFileManager, loading, selectedOrg?.id]);

  const capitalFlowIssuesView = useMemo(() => ({
    title: "Debt Issues",
    defaultSort: { key: "issue_name", asc: true },
    grouping: {
      key: "issuer",
      header: "Issuer",
      defaultCollapsed: false,
      showToggleAll: true,
      value: (record) => getIssuerName(record).trim(),
      label: (record) => getIssuerName(record).trim() || "—",
    },
    search: {
      placeholder: "Search debt issues…",
      predicate: (record, query) =>
        getIssuerName(record).toLowerCase().includes(query) ||
        getIssueName(record).toLowerCase().includes(query) ||
        String(record.allocationStatus || "").includes(query),
    },
    selectedLabel: (record) => getIssueName(record),
    columns: [
      {
        key: "issue_name",
        header: "Issue Name",
        sortValue: (record) => getIssueName(record).toLowerCase(),
        cellClassName: "w-[75%]",
        render: (record) => <div className="truncate" title={getIssueName(record)}>{getIssueName(record)}</div>,
      },
      {
        key: "allocation",
        header: "Allocation",
        sortValue: (record) => ({ none: 0, partial: 1, full: 2 }[record.allocationStatus] || 0),
        cellClassName: "w-[25%]",
        render: (record) => {
          const status = record.allocationStatus || "none";
          const styles = status === "full"
            ? "bg-green-500/15 text-green-700 dark:text-green-300"
            : status === "partial"
              ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
              : "bg-muted/40 text-muted";
          const label = status.charAt(0).toUpperCase() + status.slice(1);
          return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${styles}`} title={record.allocationHasOverage ? "Partial allocation includes one or more overallocated maturities." : undefined}>{label}</span>;
        },
      },
    ],
  }), []);

  const loadOrganization = useCallback(async () => {
    if (!selectedOrg?.id) {
      setIssues([]); setProjects([]); setClassifications([]); setProjectAssignments([]); setAllocationPortfolio({ components: [], assignments: [], amounts: [] }); setIssueId("");
      return;
    }
    setLoading(true);
    try {
      const [nextIssues, nextProjects, nextClassifications, nextProjectAssignments, nextAllocationPortfolio] = await Promise.all([
        fetchDebtStudioIssues(selectedOrg.id), fetchProjects(selectedOrg.id), fetchClassifications(selectedOrg.id), fetchAllProjectClassificationAssignments(selectedOrg.id), fetchDebtAllocationPortfolio(selectedOrg.id),
      ]);
      setIssues(nextIssues); setProjects(nextProjects); setClassifications(nextClassifications); setProjectAssignments(nextProjectAssignments); setAllocationPortfolio(nextAllocationPortfolio);
      setIssueId(""); setWorkspace({ components: [], assignments: [], amounts: [] }); setGridRows([]); setDirty(false);
    } catch (error) {
      await alert(error.message, { title: "Unable to Load Debt Allocations" });
    } finally { setLoading(false); }
  }, [alert, selectedOrg?.id]);
  useEffect(() => { loadOrganization(); }, [loadOrganization]);

  const refreshAllocationPortfolio = useCallback(async () => {
    if (!selectedOrg?.id) return;
    setAllocationPortfolio(await fetchDebtAllocationPortfolio(selectedOrg.id));
  }, [selectedOrg?.id]);

  const loadWorkspace = useCallback(async () => {
    if (!issueId || !selectedOrg?.id) {
      setWorkspace({ components: [], assignments: [], amounts: [] }); setGridRows([]); return;
    }
    setLoading(true);
    try {
      const next = await fetchDebtAllocationWorkspace({ issueId, orgId: selectedOrg.id });
      const amounts = new Map(next.amounts.map((amount) => [`${amount.component_id}:${amount.maturity_date}`, Number(amount.allocated_amount)]));
      setWorkspace(next);
      setGridRows((issue?.maturities || []).map((maturity) => {
        const row = { maturityDate: maturity.maturityDate, actualPrincipal: maturity.principalAmount };
        next.components.forEach((component) => { row[componentKey(component.id)] = amounts.get(`${component.id}:${maturity.maturityDate}`) || 0; });
        return row;
      }));
      setDirty(false);
    } catch (error) {
      await alert(error.message, { title: "Unable to Load Allocations" });
    } finally { setLoading(false); }
  }, [alert, issue?.maturities, issueId, selectedOrg?.id]);
  useEffect(() => { loadWorkspace(); }, [loadWorkspace]);

  const detailedComponents = useMemo(() => workspace.components.map((component) => ({
    ...component, details: componentDetails(component, projects, classifications, workspace.assignments),
  })), [classifications, projects, workspace.assignments, workspace.components]);
  const componentEditorAssignments = useMemo(
    () => componentEditor?.id
      ? workspace.assignments.filter(
          (assignment) => assignment.component_id === componentEditor.id
        )
      : [],
    [componentEditor, workspace.assignments]
  );

  const calculatedRows = useMemo(() => gridRows.map((row) => {
    const allocatedCents = workspace.components.reduce((sum, component) => sum + toCents(row[componentKey(component.id)] || 0), 0);
    const result = calculateAllocationStatus({ principalCents: toCents(row.actualPrincipal), allocatedCents });
    return { ...row, allocatedPrincipal: fromCents(result.allocatedCents), variance: fromCents(result.varianceCents), status: result.status };
  }), [gridRows, workspace.components]);

  const columns = useMemo(() => [
    { key: "maturityDate", label: "Maturity", editable: false, width: "w-28" },
    { key: "actualPrincipal", label: "Actual Principal", editable: false, numeric: true, width: "w-36", className: "text-right numeric", headerClassName: "text-right", format: amount },
    ...detailedComponents.map((component) => ({ key: componentKey(component.id), label: component.details.label, editable: true, numeric: true, width: "w-40", className: "text-right numeric", headerClassName: "text-right", format: amount })),
    { key: "allocatedPrincipal", label: "Allocated", editable: false, numeric: true, width: "w-36", className: "text-right numeric", headerClassName: "text-right", format: amount },
    { key: "variance", label: "Variance", editable: false, numeric: true, width: "w-32", className: "text-right numeric", headerClassName: "text-right", format: amount },
    { key: "status", label: "Status", editable: false, width: "w-32", format: (value) => String(value || "").replaceAll("_", " ") },
  ], [detailedComponents]);

  const handleGridChange = (rows) => {
    setGridRows(rows.map((row) => {
      const clean = { ...row };
      workspace.components.forEach((component) => {
        const key = componentKey(component.id);
        const amount = Number(clean[key] || 0);
        clean[key] = Number.isFinite(amount) && amount >= 0 ? amount : 0;
      });
      return clean;
    }));
    setDirty(true);
  };

  const handleSaveAmounts = async () => {
    setSaving(true);
    try {
      const allocations = gridRows.flatMap((row) => workspace.components.map((component) => ({
        component_id: component.id, maturity_date: row.maturityDate,
        source_principal_amount: Number(row.actualPrincipal), allocated_amount: Number(row[componentKey(component.id)] || 0),
      })));
      await replaceDebtAllocationAmounts({ issueId, orgId: selectedOrg.id, tenantId, allocations });
      await Promise.all([loadWorkspace(), refreshAllocationPortfolio()]);
    } catch (error) {
      await alert(error.message, { title: "Unable to Save Debt Allocations" });
    } finally { setSaving(false); }
  };

  const ensureGridSaved = async () => {
    if (!dirty) return true;
    await alert("Save or discard the current maturity allocation changes before modifying components.", { title: "Unsaved Allocations" });
    return false;
  };

  const handleIssueChange = async (nextIssueId) => {
    if (dirty) {
      const discard = await confirm(
        "Discard the unsaved maturity allocation changes and open another issue?",
        {
          title: "Unsaved Allocations",
          okLabel: "Discard",
          cancelLabel: "Cancel",
        }
      );
      if (!discard) return false;
    }
    setDirty(false);
    setIssueId(nextIssueId);
    return true;
  };

  const handleIssueSelect = async (selectedIssue) => {
    const changed = await handleIssueChange(selectedIssue?.id || "");
    if (!changed) return;
    issuesFileManager.openRecord?.(selectedIssue);
    setShowIssueManager(false);
  };

  const handleSaveComponent = async (component, assignments) => {
    setSaving(true);
    try {
      await saveDebtAllocationComponent({ component, assignments, issueId, orgId: selectedOrg.id, tenantId, userId: profile.id });
      setComponentEditor(undefined); await Promise.all([loadWorkspace(), refreshAllocationPortfolio()]);
    } catch (error) {
      await alert(error.message, { title: "Unable to Save Allocation Component" });
    } finally { setSaving(false); }
  };

  const handleDeleteComponent = async () => {
    if (!componentEditor?.id) return;
    if (!await confirm(`Delete allocation component “${componentEditor.details.label}” and all of its maturity allocations?`, { title: "Delete Allocation Component", okLabel: "Delete", cancelLabel: "Cancel" })) return;
    setSaving(true);
    try {
      await deleteDebtAllocationComponent({ componentId: componentEditor.id, orgId: selectedOrg.id });
      setComponentEditor(undefined); await Promise.all([loadWorkspace(), refreshAllocationPortfolio()]);
    } catch (error) {
      await alert(error.message, { title: "Unable to Delete Allocation Component" });
    } finally { setSaving(false); }
  };

  const summary = useMemo(() => summarizeDebtStudioIssue(issue), [issue]);
  const totalAllocated = calculatedRows.reduce((sum, row) => sum + row.allocatedPrincipal, 0);
  const totalVariance = summary.principalAmount - totalAllocated;

  return (
    <Page
      title="CapitalFlow - Debt Allocations"
      subtitle="Allocate existing DebtStudio principal to Projects and configurable classifications."
      showOrganizationSelector
    >
      {!issue ? (
        !selectedOrg ? (
          <div className="text-sm text-muted">Select an organization above to browse debt issues.</div>
        ) : (
          <FileManagerPanel
            entityName="Issue"
            adapter={capitalFlowIssueManager}
            view={capitalFlowIssuesView}
            onSelect={handleIssueSelect}
            showToolbar
            showFooter
            maxHeightClassName="max-h-[60vh]"
          />
        )
      ) : (
        <SectionCard
          title="DebtStudio Issue"
          subtitle="Principal is read from DebtStudio and grouped by maturity date."
        >
          <BrowseInput
            label="Debt Issue"
            value={issue?.name || ""}
            placeholder={loading ? "Loading issues…" : "Select an existing DebtStudio issue"}
            browseLabel="Browse…"
            browseAriaLabel="Browse DebtStudio issues"
            onBrowse={() => setShowIssueManager(true)}
            loading={loading}
            disabled={!selectedOrg || loading}
            className="w-full max-w-3xl"
          />
        </SectionCard>
      )}

      {issue ? <>
        <SectionCard title="Project Allocations" subtitle="Each component connects one Project and its classifications to this issue." headerAction={<button type="button" className="btn btn-primary flex items-center gap-2" onClick={() => setComponentEditor(null)} disabled={dirty || !projects.length}><Plus size={16} /> Add Component</button>}>
          {detailedComponents.length ? <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{detailedComponents.map((component) => <button key={component.id} type="button" className="rounded-lg border border-border p-2.5 text-left hover:border-brand" onClick={async () => { if (await ensureGridSaved()) setComponentEditor(component); }}><div className="flex items-start justify-between gap-2"><span className="text-sm font-semibold leading-tight text-app">{component.details.projectName}</span><Pencil size={13} className="mt-0.5 shrink-0 text-muted" /></div>{component.details.projectCode ? <div className="mt-0.5 text-[11px] leading-tight text-muted">{component.details.projectCode}</div> : null}<div className="mt-1 text-[11px] leading-4 text-muted">{component.details.paths.map((path) => <div key={path}>{path}</div>)}</div></button>)}</div> : <div className="py-6 text-center text-sm text-muted">Add an allocation component to begin entering Project principal.</div>}
        </SectionCard>

        <SectionCard title="Principal Allocations" subtitle={`${summary.maturityCount} maturities · ${currency(summary.principalAmount)} actual principal`} headerAction={<button type="button" className="btn btn-primary flex items-center gap-2" onClick={handleSaveAmounts} disabled={!dirty || saving || !workspace.components.length}><Save size={16} /> {saving ? "Saving…" : "Save Allocations"}</button>}>
          <div className="mb-4 grid gap-3 sm:grid-cols-3"><div><div className="text-xs uppercase text-muted">Actual Principal</div><div className="font-semibold tabular-nums text-app">{currency(summary.principalAmount)}</div></div><div><div className="text-xs uppercase text-muted">Allocated Principal</div><div className="font-semibold tabular-nums text-app">{currency(totalAllocated)}</div></div><div><div className="text-xs uppercase text-muted">Variance</div><div className={`font-semibold tabular-nums ${totalVariance < 0 ? "text-red-600" : "text-app"}`}>{currency(totalVariance)}</div></div></div>
          {workspace.components.length ? <ExcelGrid columns={columns} rows={calculatedRows} onChange={handleGridChange} /> : <div className="py-6 text-center text-sm text-muted">Create at least one allocation component to open the maturity grid.</div>}
        </SectionCard>
      </> : null}

      {componentEditor !== undefined ? <AllocationComponentModal component={componentEditor} initialAssignments={componentEditorAssignments} projects={projects} classifications={componentClassifications} projectAssignments={projectAssignments} saving={saving} onSave={handleSaveComponent} onDelete={handleDeleteComponent} onClose={() => setComponentEditor(undefined)} /> : null}
      {showIssueManager ? (
        <FileManagerModal
          entityName="Issue"
          adapter={capitalFlowIssueManager}
          view={capitalFlowIssuesView}
          onClose={() => setShowIssueManager(false)}
          onSelect={handleIssueSelect}
        />
      ) : null}
    </Page>
  );
}
